import { useState, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ModuleFieldDefinition } from '@/types/massMigration';
import {
  useMassMigrationWizardParsedCsv,
  useMassMigrationWizardExcludedRows,
  useMassMigrationWizardSelections,
  useMassMigrationWizardDetectedModules,
  useMassMigrationWizardOverrides,
  useMassMigrationWizardActions,
} from '@/stores/massMigrationWizardStore';
import { WizardNavigation } from './WizardNavigation';
import { StatTile } from './StatTile';

type ViewMode = 'byModule' | 'byOrg';

// ─── Consistency chip ─────────────────────────────────────────────────────────

interface ConsistencyChipProps {
  variantCount: number;
}

function ConsistencyChip({ variantCount }: ConsistencyChipProps) {
  if (variantCount <= 1) {
    return (
      <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        {variantCount === 0 ? 'No values' : '1 variant'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-warning/50 bg-warning/10 px-2 py-0.5 text-xs text-warning">
      {variantCount} variants
    </span>
  );
}

// ─── ByModulePanel ────────────────────────────────────────────────────────────

interface ByModulePanelProps {
  moduleKey: string;
  configFields: ModuleFieldDefinition[];
  activeRowNumbers: number[];
  orgNameByRow: Map<number, string>;
  overridesByRow: Record<number, Record<string, Record<string, string | null>>>;
  onSetOverride: (rowNumber: number, moduleKey: string, fieldKey: string, value: string | null) => void;
}

function ByModulePanel({
  moduleKey,
  configFields,
  activeRowNumbers,
  orgNameByRow,
  overridesByRow,
  onSetOverride,
}: ByModulePanelProps) {
  const [perOrgExpanded, setPerOrgExpanded] = useState(false);

  if (configFields.length === 0) {
    return (
      <div className="space-y-5">
        <h3 className="text-sm font-semibold text-foreground">{moduleKey}</h3>
        <p className="text-sm text-muted-foreground">This module has no configurable fields.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h3 className="text-sm font-semibold text-foreground">{moduleKey}</h3>

      {configFields.map(({ key: fieldKey, label }) => {
        // Collect distinct non-null values across all orgs for this module+field
        const values = activeRowNumbers
          .map((rn) => overridesByRow[rn]?.[moduleKey]?.[fieldKey] ?? null)
          .filter((v): v is string => v !== null);
        const distinctCount = new Set(values).size;

        // "Shared" field value — show the common value if all agree, else empty
        const allSame = distinctCount <= 1;
        const sharedValue = allSame && values.length > 0 ? (values[0] ?? '') : '';

        return (
          <div key={fieldKey} className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-medium text-foreground">{label}</Label>
              <ConsistencyChip variantCount={distinctCount} />
            </div>
            <div className="flex items-center gap-2">
              <Input
                id={`shared-${moduleKey}-${fieldKey}`}
                placeholder={`Shared value for ${label}…`}
                value={sharedValue}
                onChange={(e) => {
                  activeRowNumbers.forEach((rn) => {
                    onSetOverride(rn, moduleKey, fieldKey, e.target.value === '' ? null : e.target.value);
                  });
                }}
                className="max-w-xs text-sm"
              />
            </div>
          </div>
        );
      })}

      {/* Per-org overrides expandable */}
      <div className="rounded-md border border-border">
        <button
          type="button"
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50"
          onClick={() => setPerOrgExpanded((prev) => !prev)}
          aria-expanded={perOrgExpanded}
        >
          <span>Per-org overrides</span>
          <span className="text-muted-foreground">{perOrgExpanded ? '▲' : '▼'}</span>
        </button>

        {perOrgExpanded && (
          <div className="divide-y divide-border border-t border-border">
            {activeRowNumbers.map((rn) => {
              const orgName = orgNameByRow.get(rn) ?? `Row ${rn}`;
              return (
                <div key={rn} className="px-4 py-3 space-y-2">
                  <p className="text-xs font-medium text-foreground">{orgName}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {configFields.map(({ key: fieldKey, label }) => {
                      const val = overridesByRow[rn]?.[moduleKey]?.[fieldKey] ?? '';
                      return (
                        <div key={fieldKey} className="space-y-1">
                          <Label htmlFor={`override-${rn}-${moduleKey}-${fieldKey}`} className="text-xs text-muted-foreground">
                            {label}
                          </Label>
                          <Input
                            id={`override-${rn}-${moduleKey}-${fieldKey}`}
                            value={val}
                            onChange={(e) =>
                              onSetOverride(rn, moduleKey, fieldKey, e.target.value === '' ? null : e.target.value)
                            }
                            className="text-xs"
                            placeholder={`${label} for ${orgName}…`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ByOrgPanel ───────────────────────────────────────────────────────────────

interface ByOrgPanelProps {
  rowNumber: number;
  orgName: string;
  selectedModules: string[];
  configFieldsByModule: Record<string, ModuleFieldDefinition[]>;
  overridesByRow: Record<number, Record<string, Record<string, string | null>>>;
  onSetOverride: (rowNumber: number, moduleKey: string, fieldKey: string, value: string | null) => void;
}

function ByOrgPanel({
  rowNumber,
  orgName,
  selectedModules,
  configFieldsByModule,
  overridesByRow,
  onSetOverride,
}: ByOrgPanelProps) {
  return (
    <div className="space-y-5">
      <h3 className="text-sm font-semibold text-foreground">{orgName}</h3>

      {selectedModules.length === 0 && (
        <p className="text-sm text-muted-foreground">No modules selected for this organization.</p>
      )}

      {selectedModules.map((moduleKey) => {
        const fields = configFieldsByModule[moduleKey] ?? [];
        return (
          <div key={moduleKey} className="rounded-md border border-border p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{moduleKey}</p>
            {fields.length === 0 ? (
              <p className="text-sm text-muted-foreground">This module has no configurable fields.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {fields.map(({ key: fieldKey, label }) => {
                  const val = overridesByRow[rowNumber]?.[moduleKey]?.[fieldKey] ?? '';
                  return (
                    <div key={fieldKey} className="space-y-1">
                      <Label htmlFor={`byorg-${rowNumber}-${moduleKey}-${fieldKey}`} className="text-xs">
                        {label}
                      </Label>
                      <Input
                        id={`byorg-${rowNumber}-${moduleKey}-${fieldKey}`}
                        value={val}
                        onChange={(e) =>
                          onSetOverride(rowNumber, moduleKey, fieldKey, e.target.value === '' ? null : e.target.value)
                        }
                        placeholder={`${label}…`}
                        className="text-sm"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── ConfigStep ───────────────────────────────────────────────────────────────

export function ConfigStep() {
  const parsedCsv = useMassMigrationWizardParsedCsv();
  const excludedRows = useMassMigrationWizardExcludedRows();
  const selectionsByRow = useMassMigrationWizardSelections();
  const detectedModulesByRow = useMassMigrationWizardDetectedModules();
  const overridesByRow = useMassMigrationWizardOverrides();
  const { setOverride, setStep } = useMassMigrationWizardActions();

  const [viewMode, setViewMode] = useState<ViewMode>('byModule');
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [selectedRowNumber, setSelectedRowNumber] = useState<number | null>(null);

  // Active (non-excluded) rows
  const activeRows = useMemo(
    () => (parsedCsv?.rows ?? []).filter((r) => !excludedRows.includes(r.rowNumber)),
    [parsedCsv, excludedRows],
  );

  const activeRowNumbers = useMemo(() => activeRows.map((r) => r.rowNumber), [activeRows]);

  // Org name lookup by rowNumber
  const orgNameByRow = useMemo(
    () => new Map(activeRows.map((r) => [r.rowNumber, r.orgName ?? r.bridgeId])),
    [activeRows],
  );

  // Config fields per module key — same across all rows (derived from ModuleConfiguration)
  const configFieldsByModule = useMemo(() => {
    const result: Record<string, ModuleFieldDefinition[]> = {};
    for (const modules of Object.values(detectedModulesByRow)) {
      for (const dto of modules) {
        if (!(dto.moduleKey in result)) {
          result[dto.moduleKey] = dto.configFields;
        }
      }
    }
    return result;
  }, [detectedModulesByRow]);

  // Unique module keys across all active orgs
  const allModuleKeys = useMemo(() => {
    const keys = new Set<string>();
    activeRowNumbers.forEach((rn) => {
      (selectionsByRow[rn] ?? []).forEach((k) => keys.add(k));
    });
    return [...keys].sort();
  }, [activeRowNumbers, selectionsByRow]);

  // Stats
  const orgsRequiringConfig = useMemo(
    () => activeRowNumbers.filter((rn) => (selectionsByRow[rn] ?? []).length > 0).length,
    [activeRowNumbers, selectionsByRow],
  );

  const modulesToConfigure = allModuleKeys.length;

  const totalConfigurations = useMemo(
    () =>
      activeRowNumbers.reduce((sum, rn) => sum + (selectionsByRow[rn] ?? []).length, 0),
    [activeRowNumbers, selectionsByRow],
  );

  const usingDefaults = useMemo(() => {
    let count = 0;
    activeRowNumbers.forEach((rn) => {
      (selectionsByRow[rn] ?? []).forEach((moduleKey) => {
        const moduleOverrides = overridesByRow[rn]?.[moduleKey];
        const hasAnyValue =
          moduleOverrides !== undefined &&
          Object.values(moduleOverrides).some((v) => v !== null && v !== '');
        if (!hasAnyValue) count++;
      });
    });
    return count;
  }, [activeRowNumbers, selectionsByRow, overridesByRow]);

  // Default selection on first render or when collections change
  const effectiveModule = selectedModule ?? allModuleKeys[0] ?? null;
  const effectiveRow = selectedRowNumber ?? activeRows[0]?.rowNumber ?? null;

  const handleSetOverride = useCallback(
    (rowNumber: number, moduleKey: string, fieldKey: string, value: string | null) => {
      setOverride(rowNumber, moduleKey, fieldKey, value);
    },
    [setOverride],
  );

  return (
    <div className="space-y-6">
      {/* Stat tiles */}
      <div className="grid grid-cols-4 gap-3">
        <StatTile label="Orgs requiring config" value={orgsRequiringConfig} />
        <StatTile label="Modules to configure" value={modulesToConfigure} />
        <StatTile label="Total configurations" value={totalConfigurations} />
        <StatTile label="Using defaults" value={usingDefaults} />
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-1 rounded-full border border-border bg-muted p-0.5 w-fit">
        {(['byModule', 'byOrg'] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => setViewMode(mode)}
            className={cn(
              'rounded-full px-4 py-1 text-sm font-medium transition-colors',
              viewMode === mode
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            aria-pressed={viewMode === mode}
          >
            {mode === 'byModule' ? 'By Module' : 'By Org'}
          </button>
        ))}
      </div>

      {allModuleKeys.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center rounded-md border border-dashed border-border">
          <p className="text-sm text-muted-foreground">No modules selected. Go back and select at least one module.</p>
        </div>
      ) : (
        <div className="flex gap-4 min-h-[400px]">
          {/* Left rail */}
          {viewMode === 'byModule' ? (
            <div className="w-52 shrink-0 rounded-md border border-border bg-muted/20 overflow-y-auto">
              {allModuleKeys.map((key) => {
                const orgCount = activeRowNumbers.filter((rn) =>
                  (selectionsByRow[rn] ?? []).includes(key),
                ).length;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedModule(key)}
                    className={cn(
                      'flex w-full flex-col items-start px-3 py-2.5 text-left transition-colors border-b border-border last:border-b-0',
                      effectiveModule === key
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted/50',
                    )}
                    aria-pressed={effectiveModule === key}
                  >
                    <span className="text-sm font-medium">{key}</span>
                    <span className="text-xs text-muted-foreground">{orgCount} org{orgCount !== 1 ? 's' : ''}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="w-52 shrink-0 rounded-md border border-border bg-muted/20 overflow-y-auto">
              {activeRows.map((row) => {
                const orgName = row.orgName ?? row.bridgeId;
                return (
                  <button
                    key={row.rowNumber}
                    type="button"
                    onClick={() => setSelectedRowNumber(row.rowNumber)}
                    className={cn(
                      'flex w-full flex-col items-start px-3 py-2.5 text-left transition-colors border-b border-border last:border-b-0',
                      effectiveRow === row.rowNumber
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted/50',
                    )}
                    aria-pressed={effectiveRow === row.rowNumber}
                  >
                    <span className="text-sm font-medium truncate w-full">{orgName}</span>
                    <span className="text-xs text-muted-foreground font-mono">{row.bridgeId}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Right panel */}
          <div className="flex-1 rounded-md border border-border p-4 overflow-y-auto">
            {viewMode === 'byModule' && effectiveModule !== null ? (
              <ByModulePanel
                key={effectiveModule}
                moduleKey={effectiveModule}
                configFields={configFieldsByModule[effectiveModule] ?? []}
                activeRowNumbers={activeRowNumbers.filter((rn) =>
                  (selectionsByRow[rn] ?? []).includes(effectiveModule),
                )}
                orgNameByRow={orgNameByRow}
                overridesByRow={overridesByRow}
                onSetOverride={handleSetOverride}
              />
            ) : viewMode === 'byOrg' && effectiveRow !== null ? (
              <ByOrgPanel
                key={effectiveRow}
                rowNumber={effectiveRow}
                orgName={orgNameByRow.get(effectiveRow) ?? `Row ${effectiveRow}`}
                selectedModules={selectionsByRow[effectiveRow] ?? []}
                configFieldsByModule={configFieldsByModule}
                overridesByRow={overridesByRow}
                onSetOverride={handleSetOverride}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Select an item from the list.</p>
            )}
          </div>
        </div>
      )}

      <WizardNavigation
        onBack={() => setStep(3)}
        onNext={() => setStep(5)}
        nextLabel="Pre-flight Check"
      />
    </div>
  );
}
